export interface TreeNode {
  id: string
  label: string
  children?: TreeNode[]
}

/**
 * The portal's folder search (MailboxFoldersModal.searchFolders) with a plain
 * case-insensitive substring instead of `new RegExp(query, 'i')`, so "(" or
 * "[" no longer throw (PARITY Q15):
 * - a node whose label matches is kept with its whole subtree;
 * - otherwise it is kept, with only its matching descendants, when any
 *   descendant matches;
 * - everything else is pruned.
 */
export function filterTree(nodes: readonly TreeNode[], query: string): TreeNode[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return [...nodes]

  const visit = (node: TreeNode): TreeNode | null => {
    if (node.label.toLowerCase().includes(needle)) return node
    const children = (node.children ?? [])
      .map(visit)
      .filter((child): child is TreeNode => child !== null)
    return children.length > 0 ? { ...node, children } : null
  }

  return nodes.map(visit).filter((node): node is TreeNode => node !== null)
}

/** Every id that has children (to expand a whole tree or search result). */
export function branchIds(nodes: readonly TreeNode[]): Set<string> {
  const out = new Set<string>()
  const walk = (list: readonly TreeNode[]) => {
    for (const node of list) {
      if (node.children?.length) {
        out.add(node.id)
        walk(node.children)
      }
    }
  }
  walk(nodes)
  return out
}

/** Ids of the ancestors of `id`, root first; empty when not found. */
export function ancestorsOf(nodes: readonly TreeNode[], id: string): string[] {
  const path: string[] = []
  const walk = (list: readonly TreeNode[]): boolean => {
    for (const node of list) {
      if (node.id === id) return true
      if (node.children?.length) {
        path.push(node.id)
        if (walk(node.children)) return true
        path.pop()
      }
    }
    return false
  }
  return walk(nodes) ? path : []
}
